import { useEffect, useState } from 'react';
import { XMarkIcon } from "@heroicons/react/24/outline";

/** Default drawer header close control (`h-4 w-4`); Drawer positions it top/right 15px. Pass as `closeButton` when you want the same UI explicitly. */
export function DrawerCloseButton({ onClose, className = "" }) {
  return (
    <button
      type="button"
      aria-label="Close"
      onClick={onClose}
      className={`text-gray-400 hover:text-gray-500 ${className}`.trim()}
    >
      <XMarkIcon className="h-6 w-6" />
    </button>
  );
}

const Drawer = ({ 
  marginFromSidebar,
  isOpen, 
  onClose, 
  title, 
  headerContent = null,
  /** Replace the default close control (same absolute slot: top/right 15px). */
  closeButton = null,
  children, 
  submitButtonText, 
  onSubmit,
  formId, 
  size, 
  showActionButtons, 
  variant, 
  width, 
  headerDrawerRef,
  submitDisabled = false,
  footer = null,
  /** Extra classes for the main scrollable body (below optional header). */
  bodyClassName = "",
  /** Offset this drawer below the fixed mobile app header without changing desktop positioning. */
  mobileTopOffset = false,
}) => {

  const [initDrawerRef, setInitDrawerRef] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    setInitDrawerRef(true);
    if (width) {
      const currentWidth = Number(width.replace('px', ''));
      const handleResize = () => {
        setIsMobile(window.innerWidth < (currentWidth + (currentWidth / 4)));
      };
      handleResize();
      window.addEventListener('resize', handleResize);
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    // Disable scroll when drawer is open
    if (isOpen && variant !== 'inventory-products') {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.body.style.overflow = 'auto';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const getSize = () => {
    switch(size) {
      case 'sm':
        return 'lg';
      case 'lg':
        return '2xl';
      case 'xl':
        return '3xl';
      default:
        return 'lg';
    }
  };
  
  const stylesForMainBlock =
    width || marginFromSidebar
      ? {
          ...(width && marginFromSidebar
            ? isMobile
              ? { maxWidth: `calc(100% - ${marginFromSidebar}px)` }
              : { maxWidth: width }
            : width
              ? isMobile
                ? { maxWidth: '100%' }
                : { maxWidth: width }
              : {}),
  
          ...(marginFromSidebar && !isMobile
            ? { marginLeft: `${marginFromSidebar}px` }
            : {}),
        }
      : undefined;

  return (
    <>
      {variant !== 'inventory-products' && (
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity z-40"
          onClick={onClose}
        />
      )}

      <div
        className={`fixed inset-y-0 right-0 ${mobileTopOffset ? "top-14 sm:top-0" : ""} w-full max-w-${getSize()} bg-white shadow-xl transform transition-transform duration-300 ease-in-out z-50 flex flex-col`}
        style={stylesForMainBlock}
      >
        <div
          ref={initDrawerRef && headerDrawerRef ? headerDrawerRef : null}
          className={`relative shrink-0 ${headerContent || title ? "border-b border-gray-200" : "h-0 overflow-visible"}`}
        >
          {headerContent || title ? (
            <div className="px-6 py-4 pr-12">
              <div className="min-w-0">
                  {headerContent || <h2 className="text-xl font-bold text-gray-900">{title}</h2>}
              </div>
            </div>
          ) : null}
          <div className="pointer-events-auto absolute right-[15px] top-[15px] z-10">
            {closeButton ?? <DrawerCloseButton onClose={onClose} />}
          </div>
        </div>

        <div
          className={`flex-1 min-h-0 overflow-x-hidden overflow-y-auto ${bodyClassName}`.trim()}
        >
          {children}
        </div>

        {footer}

        {showActionButtons && (
          <div className="border-t border-gray-200 px-6 py-4 bg-white">
            <div className="flex gap-2">
                <button
                  form={formId}
                  onClick={() => onSubmit?.()}
                  type="submit"
                  disabled={submitDisabled}
                  className={`flex-1 font-medium py-2 px-4 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary ${
                    submitDisabled 
                      ? 'bg-gray-200 text-gray-500 cursor-not-allowed' 
                      : 'bg-primary hover:bg-primary-dark text-white'
                  }`}
                >
                  {submitButtonText}
                </button>
                <button
                  type="button"
                  className="flex-1 inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                  onClick={onClose}
                >
                  Cancel
                </button>
              </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Drawer; 